import MedicaidLeadContext from "contexts/MedicaidLeadContext";
import {
  fetchMedicaidLead,
  getMedicaidLeadCalculation,
} from "modules/medicaid/actions";
import { MedicaidLeadStatus } from "modules/medicaid/types";
import { getPartnerRoute, ROUTE_TEMPLATE } from "modules/navigation/routes";
import { formatPartnerForRoute } from "modules/navigation/helpers/getFormattedPartnerId";
import { loadPartnerDetails } from "modules/partner/actions";
import React, { useContext } from "react";
import { shallowEqual, useDispatch } from "react-redux";
import { Redirect, useParams } from "react-router-dom";
import { useSelector } from "redux/reducer";

import styles from "./styles.module.scss";
import classNames from "classnames/bind";
import TabbedButton, { TabbedButtonType } from "components/TabbedButton";
import { go } from "modules/navigation/helpers/navigator";
import AnimatedLoadingOverlay from "components/AnimatedLoadingOverlay";
import { AnimatedLoadingOverlaySize } from "components/AnimatedLoadingOverlay/types";
import Icon, { IconType } from "components/Icons";
import AssetProtectionPlanAdditionalProtection from "../AssetPrtoectionPlanAdditionalProtection";
import PlatformContext from "contexts/PlatformContext";

const cx = classNames.bind(styles);

const MedicaidLeadFinished = () => {
  const dispatch = useDispatch();
  const { lead, isLoadingMedicaidLead, analysis, isLoadingMedicaidAnalysis } =
    useContext(MedicaidLeadContext);
  const { isMobile } = useContext(PlatformContext);

  const { partner, isLoadingPartner, jurisdiction } = useSelector(
    (state) => ({
      partner: state.partner,
      isLoadingPartner: state.isLoadingPartner,
      jurisdiction: state.state,
    }),
    shallowEqual,
  );
  const { partnerId: urlPartnerId, medicaidLeadId } = useParams<{
    partnerId?: string;
    medicaidLeadId?: string;
  }>();

  if (jurisdiction === null) {
    return <Redirect to={ROUTE_TEMPLATE.ROOT} />;
  }

  if (medicaidLeadId === undefined) {
    return <Redirect to={ROUTE_TEMPLATE.ROOT} />;
  }

  if (lead === undefined) {
    if (!isLoadingMedicaidLead) {
      fetchMedicaidLead(dispatch, medicaidLeadId);
    }
    return <AnimatedLoadingOverlay size={AnimatedLoadingOverlaySize.LARGE} />;
  }

  if (lead.status === MedicaidLeadStatus.InterviewInProgress) {
    const partnerIdForRoute = urlPartnerId ?? lead.partnerId ?? "";
    const interviewRoute = getPartnerRoute(
      formatPartnerForRoute(partnerIdForRoute),
      ROUTE_TEMPLATE.PARTNER_MEDICAID_LEAD,
      { medicaidLeadId },
    );
    return <Redirect to={interviewRoute} />;
  }

  if (lead.partnerId === undefined) {
    return <Redirect to={ROUTE_TEMPLATE.ROOT} />;
  }

  if (
    partner === undefined ||
    partner === null ||
    lead.partnerId !== partner.id
  ) {
    if (!isLoadingPartner) {
      loadPartnerDetails(lead.partnerId)(dispatch);
    }
    return <AnimatedLoadingOverlay size={AnimatedLoadingOverlaySize.LARGE} />;
  }

  if (
    analysis?.calculation === undefined ||
    analysis.medicaidLeadId !== lead.medicaidLeadId
  ) {
    if (!isLoadingMedicaidAnalysis) {
      getMedicaidLeadCalculation(dispatch, lead.medicaidLeadId);
    }
    return <AnimatedLoadingOverlay size={AnimatedLoadingOverlaySize.LARGE} />;
  }

  const partnerIdForRoute = urlPartnerId ?? lead.partnerId ?? "";

  const handleClickBackToInterview = () => {
    go(
      dispatch,
      getPartnerRoute(
        formatPartnerForRoute(partnerIdForRoute),
        ROUTE_TEMPLATE.PARTNER_MEDICAID_LEAD,
        { medicaidLeadId: lead.medicaidLeadId },
      ),
    );
  };

  const handleClickSeeEstatePlanningOptions = () => {
    go(
      dispatch,
      getPartnerRoute(
        formatPartnerForRoute(partnerIdForRoute),
        ROUTE_TEMPLATE.PARTNER_MEDICAID_LEADS,
      ),
    );
  };

  return (
    <div className={cx("medicaid-lead-finished")}>
      <div className={cx("medicaid-lead-finished__header")}>
        <Icon
          type={IconType.TICK}
          className={cx("medicaid-lead-finished__tick")}
        />
        <h2>Congratulations! You took the first step...</h2>
      </div>
      <h3>
        Your Asset Protection Plan analysis has been sent to{" "}
        <b>{partner.data.displayName}</b>. They will review your information and
        contact you directly with the next steps.
      </h3>
      <div className={cx("medicaid-lead-finished__reminder")}>
        <div className={cx("medicaid-lead-finished__analysis-graph")}>
          <AssetProtectionPlanAdditionalProtection
            calculation={analysis.calculation}
            showTicks={!isMobile}
          />
        </div>
      </div>
      <div className={cx("medicaid-lead-finished__actions")}>
        <TabbedButton
          label="Back to your interview"
          style={TabbedButtonType.SECONDARY}
          onClick={handleClickBackToInterview}
        />
        <TabbedButton
          label="Back to Medicaid Qualifications"
          style={TabbedButtonType.PRIMARY}
          onClick={handleClickSeeEstatePlanningOptions}
        />
      </div>
    </div>
  );
};

export default MedicaidLeadFinished;
